import React from 'react';
import { statusToFunnelOrder } from '../data/funnelStages';

const RESPONDED_STATUSES = ['response_received', 'phone_screen', 'interview', 'final_round', 'offer'];
const STALE_THRESHOLD_MS = 14 * 24 * 60 * 60 * 1000; // 2 weeks

function getJobDate(job) {
  return new Date(job.appliedAt || job.addedAt);
}

function isApplied(job) {
  return statusToFunnelOrder(job.status) >= 1;
}

function hasResponded(job) {
  return RESPONDED_STATUSES.includes(job.status);
}

function computeInsights(jobs) {
  const now = new Date();
  const thisWeekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const lastWeekStart = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

  const appliedJobs = jobs.filter(isApplied);

  const thisWeekApplied = appliedJobs.filter(j => {
    const d = getJobDate(j);
    return d >= thisWeekStart && d <= now;
  });

  const lastWeekApplied = appliedJobs.filter(j => {
    const d = getJobDate(j);
    return d >= lastWeekStart && d < thisWeekStart;
  });

  // Volume
  const thisWeekCount = thisWeekApplied.length;
  const lastWeekCount = lastWeekApplied.length;
  const volumeDiff = thisWeekCount - lastWeekCount;

  // Response rate (across all applied jobs)
  const respondedJobs = appliedJobs.filter(hasResponded);
  const responseRate = appliedJobs.length > 0
    ? Math.round((respondedJobs.length / appliedJobs.length) * 100)
    : 0;

  // Match correlation
  const respondedWithScore = respondedJobs.filter(j => j.matchScore != null);
  const nonRespondedApplied = appliedJobs.filter(j => !hasResponded(j) && j.matchScore != null);

  const avgRespondedMatch = respondedWithScore.length > 0
    ? Math.round(respondedWithScore.reduce((sum, j) => sum + j.matchScore, 0) / respondedWithScore.length)
    : null;

  const avgNonRespondedMatch = nonRespondedApplied.length > 0
    ? Math.round(nonRespondedApplied.reduce((sum, j) => sum + j.matchScore, 0) / nonRespondedApplied.length)
    : null;

  // Stale applications (applied but no response in 2+ weeks)
  const staleApps = appliedJobs.filter(j => {
    if (hasResponded(j)) return false;
    if (j.status === 'rejected' || j.status === 'withdrawn') return false;
    const d = getJobDate(j);
    return (now.getTime() - d.getTime()) >= STALE_THRESHOLD_MS;
  });

  return {
    totalApplied: appliedJobs.length,
    thisWeekCount,
    lastWeekCount,
    volumeDiff,
    responseRate,
    avgRespondedMatch,
    avgNonRespondedMatch,
    hasMatchData: avgRespondedMatch !== null && avgNonRespondedMatch !== null,
    staleCount: staleApps.length,
  };
}

function TrendIndicator({ diff }) {
  if (diff === 0) return <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '6px' }}>same as last week</span>;
  const isUp = diff > 0;
  return (
    <span style={{ color: isUp ? '#059669' : '#ef4444', fontSize: '12px', marginLeft: '6px', fontWeight: '500' }}>
      {isUp ? '\u2191' : '\u2193'} {Math.abs(diff)} vs last week
    </span>
  );
}

function BenchmarkIndicator({ rate }) {
  if (rate >= 8 && rate <= 12) {
    return <span style={{ color: '#059669', fontSize: '11px', marginLeft: '6px' }}>on benchmark</span>;
  }
  if (rate > 12) {
    return <span style={{ color: '#059669', fontSize: '11px', marginLeft: '6px' }}>above benchmark (8-12%)</span>;
  }
  return <span style={{ color: '#f59e0b', fontSize: '11px', marginLeft: '6px' }}>below benchmark (8-12%)</span>;
}

function InsightRow({ icon, children }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '10px 0',
      borderBottom: '1px solid #f0ede4',
    }}>
      <span style={{ fontSize: '16px', flexShrink: 0, lineHeight: '1.4' }}>{icon}</span>
      <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
        {children}
      </div>
    </div>
  );
}

const WeeklyLearnings = ({ jobs = [] }) => {
  const insights = computeInsights(jobs);

  if (insights.totalApplied === 0) {
    return (
      <div style={{
        background: '#fffef9',
        borderLeft: '3px solid #f7c948',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <div style={{ fontSize: '15px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
          {'\uD83D\uDCA1'} Weekly Insights
        </div>
        <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
          Start applying to see your weekly insights
        </p>
      </div>
    );
  }

  return (
    <div style={{
      background: '#fffef9',
      borderLeft: '3px solid #f7c948',
      borderRadius: '8px',
      padding: '16px 20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <div style={{
        fontSize: '15px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}>
        {'\uD83D\uDCA1'} Weekly Insights
      </div>

      {/* Volume */}
      <InsightRow icon={'\uD83D\uDCCA'}>
        <span>
          You applied to <strong>{insights.thisWeekCount}</strong> job{insights.thisWeekCount !== 1 ? 's' : ''} this week
        </span>
        <TrendIndicator diff={insights.volumeDiff} />
      </InsightRow>

      {/* Response Rate */}
      <InsightRow icon={'\uD83D\uDCEC'}>
        <span>
          Your response rate is <strong>{insights.responseRate}%</strong>
        </span>
        <BenchmarkIndicator rate={insights.responseRate} />
      </InsightRow>

      {/* Match Correlation */}
      {insights.hasMatchData && (
        <InsightRow icon={'\uD83C\uDFAF'}>
          <span>
            Jobs that responded had <strong>{insights.avgRespondedMatch}%</strong> avg match vs{' '}
            <strong>{insights.avgNonRespondedMatch}%</strong> for no-response
          </span>
        </InsightRow>
      )}

      {/* Stale Applications */}
      {insights.staleCount > 0 && (
        <InsightRow icon={'\u23F3'}>
          <span>
            <strong>{insights.staleCount}</strong> application{insights.staleCount !== 1 ? 's' : ''} ha{insights.staleCount !== 1 ? 've' : 's'} had no response in 2+ weeks
          </span>
        </InsightRow>
      )}
    </div>
  );
};

export default WeeklyLearnings;
